import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API, rupiah, fmtDate, bankList } from "@/lib/api";
import { ArrowLeft, Printer } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import DocumentShareButton from "@/components/DocumentShareButton";
import { resolveDocumentIdentity } from "@/lib/documentIdentity";

export default function InvoiceDetail() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [settings, setSettings] = useState(null);
  const [landscape, setLandscape] = useState(false);

  useEffect(() => {
    API.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch(() => {});
    API.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [id]);

  if (!inv) return <p className="text-slate-500">Memuat invoice...</p>;
  const identity = resolveDocumentIdentity(inv, settings || {});

  const shareLines = [
    { text: identity.name, size: 12, bold: true, align: "center" },
    { text: `WhatsApp: ${identity.whatsapp}`, size: 9, align: "center", muted: true },
    { text: "INVOICE", size: 22, bold: true, align: "center", gap: 4 },
    { text: inv.invoice_number, size: 12, bold: true, align: "center", gap: 12 },
    { rule: true },
    { text: `Tanggal: ${fmtDate(inv.created_at)}`, muted: true },
    ...(inv.due_date ? [{ text: `Jatuh tempo: ${fmtDate(inv.due_date)}`, muted: true }] : []),
    { text: "", spacer: true },
    { text: "DITAGIHKAN KEPADA", size: 10, bold: true },
    { text: inv.customer_name, size: 12, bold: true },
    ...(inv.customer_phone ? [{ text: `Telepon: ${inv.customer_phone}` }] : []),
    ...(inv.customer_address ? [{ text: `Alamat: ${inv.customer_address}` }] : []),
    { text: "", spacer: true },
    { text: "RINCIAN TAGIHAN", size: 11, bold: true },
    { rule: true },
    ...inv.items.flatMap((item, index) => [
      { text: `${index + 1}. ${item.description}`, bold: true },
      { text: `   ${item.qty} x ${rupiah(item.price)} = ${rupiah(item.qty * item.price)}`, muted: true, gap: 4 },
    ]),
    { rule: true },
    { text: `Subtotal: ${rupiah(inv.subtotal)}`, align: "right" },
    { text: `Pajak (${inv.tax_percent}%): ${rupiah(inv.tax)}`, align: "right" },
    ...((inv.discount || 0) > 0 ? [{ text: `Diskon: - ${rupiah(inv.discount)}`, align: "right" }] : []),
    { text: `TOTAL: ${rupiah(inv.total)}`, size: 13, bold: true, align: "right", gap: 8 },
    { text: inv.status === "lunas" ? "STATUS: LUNAS" : "STATUS: BELUM LUNAS", bold: true, align: "right" },
    ...(inv.notes ? [{ text: `Catatan: ${inv.notes}`, gap: 8 }] : []),
    ...bankList(settings).map((bank) => ({ text: `Pembayaran: ${bank.bank_name} - ${bank.account_number} a.n. ${bank.holder}` })),
    { text: "", spacer: true, gap: 26 },
    { text: "Hormat kami,", align: "center" },
    { text: identity.name, bold: true, align: "center" },
  ];

  return (
    <div>
      <div className="no-print flex items-center flex-wrap gap-3 mb-6">
        <Link to="/admin/invoice" data-testid="back-invoices" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0B1F3A]">
          <ArrowLeft className="h-4 w-4" /> Kembali
        </Link>
        <div className="flex gap-2" data-testid="orientation-toggle">
          {[{ v: false, l: "Potret" }, { v: true, l: "Lanskap" }].map((o) => (
            <button
              key={o.l}
              onClick={() => setLandscape(o.v)}
              data-testid={`orient-${o.l.toLowerCase()}`}
              className={`px-4 py-2.5 text-xs font-bold rounded-md border transition-colors ${landscape === o.v ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "border-slate-300 text-slate-600 hover:border-[#0B1F3A]"}`}
            >
              {o.l}
            </button>
          ))}
        </div>
        <button onClick={() => window.print()} data-testid="print-invoice" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B1F3A] text-white text-sm font-bold rounded-md">
          <Printer className="h-4 w-4" /> Cetak / Simpan PDF
        </button>
        <DocumentShareButton
          filename={`${inv.invoice_number || "invoice-nawadah"}.pdf`}
          title={`Invoice ${inv.invoice_number}`}
          message={`Halo ${inv.customer_name}, berikut invoice ${inv.invoice_number} dari ${identity.name}.`}
          phone={inv.customer_phone || ""}
          lines={shareLines}
          testid="share-invoice-whatsapp"
        />
      </div>
      <style>{`@page { size: ${landscape ? "landscape" : "portrait"}; margin: 8mm; }`}</style>

      <div className="print-area bg-white border border-slate-200 rounded-md max-w-3xl mx-auto p-10 print:max-w-full print:p-6 print:border-0" data-testid="invoice-print-area">
        <div className="flex items-start justify-between pb-6 border-b-2 border-[#0B1F3A]">
          <div className="flex items-center gap-4">
            <img src={identity.logo} alt={`Logo ${identity.name}`} className="h-16 w-16 object-contain print:h-28 print:w-28" />
            <div className="max-w-[260px]">
              <p className="font-display text-xl font-bold leading-tight text-[#0B1F3A]">{identity.name}</p>
              <p className="text-xs text-slate-500 mt-1">Indonesia • WA: {identity.whatsapp}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-display text-2xl font-bold text-[#0B1F3A]">INVOICE</p>
            <p className="font-mono-accent text-sm font-bold">{inv.invoice_number}</p>
            <p className="text-xs text-slate-500">Tanggal: {fmtDate(inv.created_at)}</p>
            {inv.due_date && <p className="text-xs text-slate-500">Jatuh tempo: {fmtDate(inv.due_date)}</p>}
          </div>
        </div>

        <div className="py-6">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Ditagihkan Kepada</p>
          <p className="font-bold">{inv.customer_name}</p>
          {inv.customer_phone && <p className="text-sm text-slate-500">{inv.customer_phone}</p>}
          {inv.customer_address && <p className="text-sm text-slate-500">{inv.customer_address}</p>}
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-y border-slate-300 text-left text-xs uppercase tracking-widest text-slate-500">
              <th className="py-2.5">Deskripsi</th>
              <th className="py-2.5 text-center w-20">Qty</th>
              <th className="py-2.5 text-right w-32">Harga</th>
              <th className="py-2.5 text-right w-32">Jumlah</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {inv.items.map((it, i) => (
              <tr key={i}>
                <td className="py-3">{it.description}</td>
                <td className="py-3 text-center">{it.qty}</td>
                <td className="py-3 text-right">{rupiah(it.price)}</td>
                <td className="py-3 text-right font-semibold">{rupiah(it.qty * it.price)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-6 flex justify-end">
          <div className="w-64 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{rupiah(inv.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Pajak ({inv.tax_percent}%)</span><span>+ {rupiah(inv.tax)}</span></div>
            {(inv.discount || 0) > 0 && (
              <div className="flex justify-between" data-testid="invoice-discount-row">
                <span className="text-slate-500">Diskon</span>
                <span className="text-red-600">- {rupiah(inv.discount)}</span>
              </div>
            )}
            <div className="flex justify-between border-t-2 border-[#0B1F3A] pt-2 font-bold text-base">
              <span>Total</span><span className="text-[#0B1F3A]">{rupiah(inv.total)}</span>
            </div>
            <p className={`text-right text-xs font-bold ${inv.status === "lunas" ? "text-emerald-600" : "text-amber-600"}`}>
              {inv.status === "lunas" ? "LUNAS" : "BELUM LUNAS"}
            </p>
          </div>
        </div>

        {inv.notes && <p className="mt-6 text-xs text-slate-500">Catatan: {inv.notes}</p>}
        {bankList(settings).length > 0 && (
          <div className="mt-4 space-y-1 text-xs text-slate-500">
            {bankList(settings).map((bank, index) => <p key={index}>Pembayaran: {bank.bank_name} — {bank.account_number} a.n. {bank.holder}</p>)}
          </div>
        )}
        <div className="mt-10 grid grid-cols-2 gap-6 text-center text-xs text-slate-600" data-testid="invoice-signature">
          <div>
            <p>Hormat Kami,</p>
            <SignaturePad />
            <p className="border-t border-slate-400 pt-2 font-bold">{identity.name}</p>
          </div>
          <div>
            <p>Pembeli (Pelanggan),</p>
            <SignaturePad storageKey="sig_invoice_pembeli" />
            <p className="border-t border-slate-400 pt-2 font-bold">{inv.customer_name || "(....................)"}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
